import React, { FC } from 'react'
import { Alert, Box, Chip, Grid, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { DataGridPremium, GridColDef, GridRowId } from '@mui/x-data-grid-premium'
import pluralize from 'pluralize'
import { ContractorTransferPreview, Gateway, Sensor } from 'src/api'
import { parseSearchIdentifiers } from 'src/helpers'
import { useGatewaysListRequest, useSensorListRequest } from 'src/hooks/api'
import { useStableCount, useWizardGrid, WizardFooter } from './wizardParts'
import {
  DeviceProject,
  DeviceSelectionState,
  filterGateways,
  getActiveGatewayProjects,
  getActiveSensorProjects,
  getDeviceSelectionState,
  TransferWizardAction,
  TransferWizardState,
  TransferWizardStep
} from './transferWizardHelpers'

const GATEWAYS_LIMIT = 1000

const SelectionChip: FC<{ selection: DeviceSelectionState; isSelected: boolean }> = ({ selection, isSelected }) => {
  const chip =
    selection.kind === 'following' ? (
      <Chip size="small" color="success" label="Follows a project" />
    ) : selection.kind === 'staying' ? (
      <Chip size="small" color="default" label="Stays at the source" />
    ) : (
      <Chip size="small" color={isSelected ? 'primary' : 'default'} variant={isSelected ? 'filled' : 'outlined'} label={isSelected ? 'Added' : 'Free'} />
    )

  return selection.hint ? (
    <Tooltip title={selection.hint}>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  )
}

const projectNames = (projects: DeviceProject[]): string => projects.map((project) => project.name).join(', ') || '-'

interface DevicesStepProps {
  state: TransferWizardState
  dispatch: (action: TransferWizardAction) => void
  preview?: ContractorTransferPreview
  isPreviewLoading: boolean
}

/**
 * Step 4: the sensors and gateways of the source. Devices that follow the moved projects are checked and locked, devices
 * that serve a staying project are disabled, and any other free device can be added, including from a pasted IMEI list.
 */
export const DevicesStep: FC<DevicesStepProps> = ({ state, dispatch, preview, isPreviewLoading }) => {
  const grid = useWizardGrid()
  const movingProjectIds = (preview?.projects || []).map((project) => project.id)
  const { identifiers, truncated } = parseSearchIdentifiers(grid.debouncedSearch)

  const sensorsRequest = useSensorListRequest({
    params: { contractorId: state.source?.id, ...grid.requestParams },
    options: { keepPreviousData: true }
  })
  const sensorCount = useStableCount(sensorsRequest.data?.count)
  const sensors = sensorsRequest.data?.rows || []
  const sensorSelection = new Map(sensors.map((sensor) => [sensor.id, getDeviceSelectionState(getActiveSensorProjects(sensor), movingProjectIds)]))
  const followingSensorIds = sensors.filter((sensor) => sensorSelection.get(sensor.id)?.kind === 'following').map((sensor) => sensor.id)

  const gatewaysRequest = useGatewaysListRequest({
    params: { contractorId: state.source?.id, limit: GATEWAYS_LIMIT, offset: 0 },
    options: { enabled: Boolean(state.source?.id) }
  })
  const allGateways = gatewaysRequest.data?.rows || []
  const { rows: gateways, notFound: gatewaysNotFound } = filterGateways(allGateways, identifiers)
  const gatewaySelection = new Map(allGateways.map((gateway) => [gateway.id, getDeviceSelectionState(getActiveGatewayProjects(gateway), movingProjectIds)]))
  const followingGatewayIds = allGateways.filter((gateway) => gatewaySelection.get(gateway.id)?.kind === 'following').map((gateway) => gateway.id)

  // a pasted list: report what is neither a sensor nor a gateway of the source
  const sensorsNotFound = identifiers.length > 1 ? sensorsRequest.data?.notFound || [] : []
  const notFound = identifiers.length > 1 ? sensorsNotFound.filter((identifier) => gatewaysNotFound.includes(identifier)) : []

  const sensorColumns: GridColDef<Sensor>[] = [
    {
      field: 'transfer',
      headerName: 'Transfer',
      minWidth: 170,
      sortable: false,
      valueGetter: ({ row }) => sensorSelection.get(row.id)?.kind,
      renderCell: ({ row }) => <SelectionChip selection={sensorSelection.get(row.id) ?? { kind: 'free' }} isSelected={state.extraSensorIds.includes(row.id)} />
    },
    { field: 'uniqueId', headerName: 'ID', minWidth: 120, sortable: false },
    { field: 'imei', headerName: 'IMEI', minWidth: 170, sortable: false, valueGetter: ({ row }) => row.imei || '-' },
    { field: 'devEUI', headerName: 'Dev EUI', minWidth: 170, sortable: false, valueGetter: ({ row }) => row.devEUI || '-' },
    { field: 'state', headerName: 'State', minWidth: 110, sortable: false, valueGetter: ({ row }) => row.state?.name || '-' },
    { field: 'projects', headerName: 'Projects', minWidth: 220, flex: 1, sortable: false, valueGetter: ({ row }) => projectNames(getActiveSensorProjects(row)) }
  ]

  const gatewayColumns: GridColDef<Gateway>[] = [
    {
      field: 'transfer',
      headerName: 'Transfer',
      minWidth: 170,
      sortable: false,
      valueGetter: ({ row }) => gatewaySelection.get(row.id)?.kind,
      renderCell: ({ row }) => <SelectionChip selection={gatewaySelection.get(row.id) ?? { kind: 'free' }} isSelected={state.extraGatewayIds.includes(row.id)} />
    },
    { field: 'deviceId', headerName: 'Device ID', minWidth: 170, sortable: false, valueGetter: ({ row }) => row.deviceId || row.devEUI || '-' },
    { field: 'name', headerName: 'Name', minWidth: 160, sortable: false, valueGetter: ({ row }) => row.name || '-' },
    { field: 'state', headerName: 'State', minWidth: 110, sortable: false, valueGetter: ({ row }) => row.state?.name || '-' },
    { field: 'projects', headerName: 'Projects', minWidth: 220, flex: 1, sortable: false, valueGetter: ({ row }) => projectNames(getActiveGatewayProjects(row)) }
  ]

  const followingProbes = preview?.devices.wagnerProbes || []
  const followingLoggers = preview?.devices.dataLoggers || []
  const followingCount = followingSensorIds.length + followingGatewayIds.length

  return (
    <>
      <Box mb={2}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          size="small"
          label="Search one device, or paste several IMEIs / IDs separated by spaces, commas or new lines"
          value={grid.search}
          onChange={(event) => grid.setSearch(event.target.value)}
        />
      </Box>
      {truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only the first 500 unique identifiers were searched.
        </Alert>
      )}
      {notFound.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Not a sensor or gateway of the source: ${notFound.join(', ')}`}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" mb={2}>
        {isPreviewLoading
          ? 'Working out which devices follow the moved projects…'
          : `Devices on this page that follow the moved projects are checked and locked (${followingCount}). Check free devices to add them; ${pluralize(
              'device',
              state.extraSensorIds.length + state.extraGatewayIds.length,
              true
            )} added so far.`}
      </Typography>

      <Typography variant="h6" mb={1}>
        Sensors
      </Typography>
      <Paper sx={{ width: '100%', minWidth: 0, mb: 3 }}>
        <DataGridPremium<Sensor>
          rows={sensors}
          columns={sensorColumns}
          autoHeight
          checkboxSelection
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          isRowSelectable={({ row }) => sensorSelection.get(row.id)?.kind === 'free'}
          rowSelectionModel={[...state.extraSensorIds, ...followingSensorIds]}
          onRowSelectionModelChange={(ids: GridRowId[]) => dispatch({ type: 'setExtraSensors', ids: ids.map(Number).filter((id) => !followingSensorIds.includes(id)) })}
          loading={sensorsRequest.isFetching || isPreviewLoading}
          pagination
          paginationMode="server"
          rowCount={sensorCount}
          paginationModel={grid.paginationModel}
          onPaginationModelChange={grid.onPaginationModelChange}
          pageSizeOptions={[25, 50, 100, 500]}
          sx={{ border: 'none' }}
        />
      </Paper>

      <Typography variant="h6" mb={1}>
        Gateways
      </Typography>
      <Paper sx={{ width: '100%', minWidth: 0, mb: 3 }}>
        <DataGridPremium<Gateway>
          rows={gateways}
          columns={gatewayColumns}
          autoHeight
          checkboxSelection
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          isRowSelectable={({ row }) => gatewaySelection.get(row.id)?.kind === 'free'}
          rowSelectionModel={[...state.extraGatewayIds, ...followingGatewayIds]}
          onRowSelectionModelChange={(ids: GridRowId[]) => dispatch({ type: 'setExtraGateways', ids: ids.map(Number).filter((id) => !followingGatewayIds.includes(id)) })}
          loading={gatewaysRequest.isFetching || isPreviewLoading}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 'none' }}
        />
      </Paper>

      {(followingProbes.length > 0 || followingLoggers.length > 0) && (
        <Grid container spacing={2}>
          {[
            { title: 'Wagner probes that follow the projects', items: followingProbes },
            { title: 'Data loggers that follow the projects', items: followingLoggers }
          ]
            .filter((group) => group.items.length)
            .map((group) => (
              <Grid item xs={12} sm={6} key={group.title}>
                <Typography variant="subtitle2" mb={1}>
                  {group.title}
                </Typography>
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                  {group.items.map((item) => (
                    <Chip key={item.id} size="small" label={item.label} />
                  ))}
                </Stack>
              </Grid>
            ))}
        </Grid>
      )}

      <WizardFooter
        hint="A device that also serves a project staying at the source stays with the source and keeps working."
        onBack={() => dispatch({ type: 'goTo', step: TransferWizardStep.Projects })}
        nextDisabled={isPreviewLoading}
        onNext={() => dispatch({ type: 'goTo', step: TransferWizardStep.Summary })}
      />
    </>
  )
}
